'use client';

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { User, LogOut, Shield, Search, MessageSquare } from 'lucide-react';
import { signOut, useSession } from 'next-auth/react';
import { Link } from '@/lib/i18n/navigation';
import { LogoPhi } from '@/components/brand/logo-phi';
import { SideNav } from '@/components/layout/side-nav';
import { CartIcon } from '@/components/cart/cart-icon';
import { MiniCart } from '@/components/cart/mini-cart';
import { LocaleSwitcher } from '@/components/layout/locale-switcher';
import { ThemeToggle } from '@/components/theme/theme-toggle';
import { cn } from '@/lib/utils';

interface HeaderProps {
  /** Home arranca transparente sobre hero. Otras páginas siempre solid. */
  transparentOnTop?: boolean;
}

const ADMIN_ROLES = new Set([
  'readonly_analyst',
  'staff_support',
  'staff_content',
  'staff_ops',
  'admin',
  'superadmin',
]);

export function Header({ transparentOnTop = false }: HeaderProps) {
  const t = useTranslations('nav');
  const tHeader = useTranslations('header');
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [lastY, setLastY] = useState(0);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const { data: session } = useSession();
  const user = session?.user;
  const role = user?.role as string | undefined;
  const isStaff = role ? ADMIN_ROLES.has(role) : false;

  useEffect(() => {
    let rAf = 0;
    const onScroll = () => {
      cancelAnimationFrame(rAf);
      rAf = requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 40);
        if (y > 120 && y > lastY) {
          setHidden(true);
        } else {
          setHidden(false);
        }
        setLastY(y);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(rAf);
    };
  }, [lastY]);

  const solid = scrolled || !transparentOnTop;

  return (
    <header
      className={cn(
        'sticky top-0 z-[var(--z-sticky)] w-full transition-[transform,background-color,border-color] duration-280',
        solid
          ? 'border-b border-[var(--color-border)] bg-[var(--color-bg)]/95 backdrop-blur-md'
          : 'border-b border-transparent bg-transparent',
        hidden ? '-translate-y-full' : 'translate-y-0'
      )}
    >
      <div className="grid h-16 w-full grid-cols-[auto_1fr_auto] items-center gap-4 px-4 md:h-20 md:px-10 lg:px-16">
        {/* Izquierda: hamburger + wordmark */}
        <div className="flex items-center gap-3">
          <SideNav />
          <Link
            href="/"
            aria-label={t('ariaHome')}
            className="flex items-center gap-2 transition-opacity hover:opacity-85"
          >
            <LogoPhi size={28} tone="gold" />
            <span className="type-h4 hidden tracking-[0.18em] text-[var(--color-fg)] sm:inline">
              NQORO
            </span>
          </Link>
        </div>

        {/* Centro: buscar global */}
        <div className="flex items-center justify-center">
          <Link
            href="/buscar"
            aria-label={t('ariaSearch')}
            className="group hidden items-center gap-3 py-2 md:flex"
          >
            <Search
              className="h-4 w-4 text-[var(--color-fg-muted)] transition-colors group-hover:text-[var(--color-accent)]"
              strokeWidth={1.25}
              aria-hidden
            />
            <span className="type-nav text-[var(--color-fg-muted)] transition-colors group-hover:text-[var(--color-accent)]">
              {t('search')}
            </span>
            <span className="block h-px w-32 bg-[var(--color-border-strong)] transition-colors group-hover:bg-[var(--color-accent)]" />
          </Link>
        </div>

        {/* Derecha: actions */}
        <div className="flex items-center gap-1 sm:gap-4">
          <Link
            href="/buscar"
            aria-label={t('search')}
            className="flex h-10 w-10 items-center justify-center rounded-sm hover:bg-[var(--color-surface)] md:hidden"
          >
            <Search className="h-5 w-5 text-[var(--color-fg-muted)]" strokeWidth={1.25} />
          </Link>
          <Link
            href="/ayuda"
            aria-label={t('assistant')}
            className="flex h-10 w-10 items-center justify-center rounded-sm hover:bg-[var(--color-surface)]"
          >
            <MessageSquare
              className="h-5 w-5 text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-accent)]"
              strokeWidth={1.25}
            />
          </Link>
          <ThemeToggle className="hidden lg:inline-flex" />
          <LocaleSwitcher className="hidden lg:inline-flex" />
          {user ? (
            <div className="relative hidden lg:block">
              <button
                type="button"
                onClick={() => setAccountMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={accountMenuOpen}
                className="type-nav flex items-center gap-2 text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-accent)]"
              >
                <User className="h-4 w-4" strokeWidth={1.25} />
                <span>{user.name || user.email?.split('@')[0] || t('account')}</span>
              </button>
              {accountMenuOpen ? (
                <>
                  <button
                    type="button"
                    aria-label={t('closeMenu')}
                    onClick={() => setAccountMenuOpen(false)}
                    className="fixed inset-0 z-[var(--z-overlay)] cursor-default bg-transparent"
                  />
                  <div
                    role="menu"
                    className="absolute top-full right-0 z-[var(--z-popover)] mt-2 min-w-[220px] border border-[var(--color-border-strong)] bg-[var(--color-bg)] shadow-lg"
                  >
                    <div className="border-b border-[var(--color-border)] px-4 py-3">
                      <p className="type-caption text-[var(--color-fg-subtle)]">
                        {tHeader('sessionAs')}
                      </p>
                      <p className="type-body-sm truncate text-[var(--color-fg)]">{user.email}</p>
                      {role ? (
                        <p className="type-eyebrow mt-1 text-[var(--color-fg-subtle)]">{role}</p>
                      ) : null}
                    </div>
                    <ul className="py-2">
                      <li>
                        <Link
                          href="/cuenta"
                          role="menuitem"
                          onClick={() => setAccountMenuOpen(false)}
                          className="type-nav block px-4 py-2 text-[var(--color-fg-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-accent)]"
                        >
                          {tHeader('myAccount')}
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/cuenta/pedidos"
                          role="menuitem"
                          onClick={() => setAccountMenuOpen(false)}
                          className="type-nav block px-4 py-2 text-[var(--color-fg-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-accent)]"
                        >
                          {tHeader('myOrders')}
                        </Link>
                      </li>
                      <li>
                        <Link
                          href="/cuenta/wishlist"
                          role="menuitem"
                          onClick={() => setAccountMenuOpen(false)}
                          className="type-nav block px-4 py-2 text-[var(--color-fg-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-accent)]"
                        >
                          {tHeader('myWishlist')}
                        </Link>
                      </li>
                      {isStaff ? (
                        <li>
                          <a
                            href="/admin"
                            role="menuitem"
                            onClick={() => setAccountMenuOpen(false)}
                            className="type-nav flex items-center gap-2 px-4 py-2 text-[var(--color-accent)] hover:bg-[var(--color-surface)] hover:text-[var(--color-accent)]"
                          >
                            <Shield className="h-4 w-4" strokeWidth={1.25} />
                            {tHeader('adminPanel')}
                          </a>
                        </li>
                      ) : null}
                      <li className="mt-2 border-t border-[var(--color-border)] pt-2">
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => {
                            setAccountMenuOpen(false);
                            void signOut({ callbackUrl: '/' });
                          }}
                          className="type-nav flex w-full items-center gap-2 px-4 py-2 text-left text-[var(--color-fg-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-danger)]"
                        >
                          <LogOut className="h-4 w-4" strokeWidth={1.25} />
                          {t('logout')}
                        </button>
                      </li>
                    </ul>
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            <Link
              href="/login"
              aria-label={t('login')}
              className="type-nav hidden items-center gap-2 text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-accent)] lg:flex"
            >
              <User className="h-4 w-4" strokeWidth={1.25} />
              <span>{t('login')}</span>
            </Link>
          )}
          <a
            href="mailto:contacto@nqoro.com"
            className="type-nav hidden items-center text-[var(--color-fg-muted)] transition-colors hover:text-[var(--color-accent)] lg:flex"
          >
            {t('contact')}
          </a>
          <CartIcon onClick={() => setMiniCartOpen(true)} />
        </div>
      </div>
      <MiniCart open={miniCartOpen} onOpenChange={setMiniCartOpen} />
    </header>
  );
}
